import { setOnStorage, getFromStorage } from '@libs/utils/helpers/handleStorage';
import { isAndroid, isIOS } from '@libs/utils/helpers/mobileOS';
import type { InstalledApp } from '../types/appbridge';
import { useRedline } from '@libs/redline';
import { BridgeType } from '../context';

const capraStorageKey = 'capra';

type CapraStorage = {
  apps: InstalledApp[];
  eventSent: boolean;
};

const getCapraStorage = (): CapraStorage => getFromStorage<CapraStorage>(capraStorageKey) ?? { apps: [], eventSent: false };
const setCapraStorage = (data: Partial<CapraStorage>) => setOnStorage('session', capraStorageKey, { ...getCapraStorage(), ...data });

export const useCapra = (bridge: BridgeType) => {
  const { getAndroidInstalledApps, getIOSInstalledApps } = bridge;
  const { track } = useRedline();

  const trackIfNeeded = (document: string, apps: InstalledApp[]) => {
    const { eventSent } = getCapraStorage();
    if (!document || apps.length === 0 || eventSent) return;
    if (!track?.capra) {
      console.warn('[CAPRA] Redline track not available — skipping appTracked event');
      return;
    }

    try {
      track.capra.appTracked({ document, apps });
      setCapraStorage({ eventSent: true });
    } catch (err) {
      console.error('[CAPRA] Failed to track appTracked event:', err);
    }
  };

  const getInstalledApps = async (document: string): Promise<InstalledApp[]> => {
    const { apps: cachedApps } = getCapraStorage();

    if (cachedApps.length > 0) {
      trackIfNeeded(document, cachedApps);
      return cachedApps;
    }

    let apps: InstalledApp[] = [];
    if (isAndroid()) apps = (await getAndroidInstalledApps?.()) ?? [];
    if (isIOS()) apps = (await getIOSInstalledApps?.()) ?? [];

    setCapraStorage({ apps });
    trackIfNeeded(document, apps);

    return apps;
  };

  return { getAndroidInstalledApps, getIOSInstalledApps, getInstalledApps };
};
